/**
 * web-client — minimal HTTP+SSE client for the BotWork web task API.
 * Completely independent of BotworkClient (WebSocket/wallet-shaped).
 *
 * API base: NEXT_PUBLIC_API_BASE (defaults to https://api.botwork.network).
 * All requests use credentials:'include' so the HttpOnly bw_session cookie
 * is sent automatically.
 */

import type {
  RunTaskOptions,
  TaskEvent,
  TaskEventType,
  TaskResult,
  StageLabel,
  AuthUser,
  TaskHistoryItem,
} from "./types";

export type {
  RunTaskOptions,
  TaskEvent,
  TaskEventType,
  TaskResult,
  StageLabel,
  AuthUser,
  TaskHistoryItem,
};
export * from "./types";

function apiBase(): string {
  return (
    process.env.NEXT_PUBLIC_API_BASE ?? "https://api.botwork.network"
  );
}

// ── Stage → plain-language mapper ─────────────────────────────────

const TYPE_LABEL: Partial<Record<TaskEventType, StageLabel>> = {
  matching: "Agent claimed your task",
  dispatch: "Agent claimed your task",
  router: "Reading your input…",
  done: "Done ✓",
};

const STAGE_LABEL: Record<string, StageLabel> = {
  CHAIN_DESIGNER: "Designing the approach…",
  PM_REVIEW: "Reviewing the plan…",
  FRONTEND: "Doing the work…",
  GENERATION: "Doing the work…",
  REVIEW: "Checking the result…",
  checking: "Checking the result…",
};

const SUBTASK_PROGRESS_LABEL: StageLabel = "Doing the work…";

/**
 * Maps a raw TaskEvent to a plain-language label for Screen 2.
 * Falls back to event.message when no mapping fits, then to null.
 */
export function mapEventToLabel(event: TaskEvent): string | null {
  // Terminal error and credits events have their own treatment in the UI layer
  if (event.type === "error") return null;
  if (event.type === "credits") return null;

  // Direct type-level mapping
  const typeLabel = TYPE_LABEL[event.type];
  if (typeLabel) return typeLabel;

  // Stage-level mapping (for subtask_* events)
  if (event.stage) {
    const stageKey = event.stage.toUpperCase();
    const mapped = STAGE_LABEL[stageKey];
    if (mapped) return mapped;
  }

  // subtask_progress / subtask_start / subtask_complete default
  if (
    event.type === "subtask_start" ||
    event.type === "subtask_progress" ||
    event.type === "subtask_complete"
  ) {
    return SUBTASK_PROGRESS_LABEL;
  }

  // Fall back to raw message
  return event.message ?? null;
}

// ── SSE parser ─────────────────────────────────────────────────────

function* parseSSEChunk(
  buffer: string,
  newChunk: string
): Generator<{ buffer: string; event: TaskEvent | null }> {
  const combined = buffer + newChunk;
  const parts = combined.split("\n\n");

  // The last part may be an incomplete event; keep it in buffer
  const pending = parts.pop() ?? "";

  for (const part of parts) {
    const dataLine = part
      .split("\n")
      .find((l) => l.startsWith("data: "));
    if (!dataLine) continue;
    const json = dataLine.slice(6).trim();
    if (!json) continue;
    try {
      const event = JSON.parse(json) as TaskEvent;
      yield { buffer: pending, event };
    } catch {
      // malformed JSON — skip
      yield { buffer: pending, event: null };
    }
  }

  // Yield the pending buffer state with no event so the caller can
  // update their buffer reference
  yield { buffer: pending, event: null };
}

// ── runTask — async generator ─────────────────────────────────────

export interface RunTaskResult {
  taskId: string | null;
  events: AsyncIterable<TaskEvent>;
}

/**
 * Submits a task and returns an async iterable of typed SSE events.
 * The X-Task-Id header is captured as soon as the response arrives.
 *
 * Throws { code: 'gate_required' } on 402.
 * Throws { code: 'bad_request', message } on 400.
 * Throws { code: 'network_error', message } on fetch failure.
 */
export async function runTask(
  prompt: string,
  opts?: RunTaskOptions
): Promise<RunTaskResult> {
  const body: Record<string, unknown> = { prompt };
  if (opts?.outputFormat) body.outputFormat = opts.outputFormat;
  if (opts?.agentTier) body.agentTier = opts.agentTier;
  if (opts?.attachments) body.attachments = opts.attachments;

  let response: Response;
  try {
    response = await fetch(`${apiBase()}/api/web/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw { code: "network_error", message: String(err) };
  }

  if (response.status === 402) {
    // Try to read body: authed users get {error:'insufficient_credits',credits,required}
    // Anonymous users get gate_required (no body or different shape)
    try {
      const body = (await response.json()) as {
        error?: string;
        credits?: number;
        required?: number;
      };
      if (body.error === "insufficient_credits") {
        throw { code: "insufficient_credits", credits: body.credits, required: body.required };
      }
    } catch (parseErr) {
      // If body parse threw the insufficient_credits error, rethrow it
      const e = parseErr as { code?: string };
      if (e?.code === "insufficient_credits") throw parseErr;
    }
    throw { code: "gate_required" };
  }

  if (response.status === 400) {
    let msg = "Bad request";
    try {
      const j = (await response.json()) as { error?: string };
      msg = j.error ?? msg;
    } catch { /* empty */ }
    throw { code: "bad_request", message: msg };
  }

  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }

  // Capture task ID from header before consuming the body
  const taskId = response.headers.get("X-Task-Id");

  if (!response.body) {
    throw { code: "no_body" };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  async function* generateEvents(): AsyncIterable<TaskEvent> {
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const gen = parseSSEChunk(buffer, chunk);

      for (const { buffer: newBuf, event } of gen) {
        buffer = newBuf;
        if (event) {
          yield event;
          // Stop after terminal events
          if (event.type === "done" || event.type === "error") return;
        }
      }
    }
  }

  return { taskId, events: generateEvents() };
}

// ── getResult ──────────────────────────────────────────────────────

export async function getResult(taskId: string): Promise<TaskResult> {
  const response = await fetch(
    `${apiBase()}/api/web/tasks/${taskId}/result`,
    { credentials: "include" }
  );
  if (response.status === 404) {
    throw { code: "not_found" };
  }
  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }
  return response.json() as Promise<TaskResult>;
}

// ── artifactUrl ────────────────────────────────────────────────────

export function artifactUrl(taskId: string, filename: string): string {
  return `${apiBase()}/api/web/tasks/${taskId}/artifacts/${encodeURIComponent(filename)}`;
}

// ── googleAuthUrl ───────────────────────────────────────────────────

export function googleAuthUrl(): string {
  return `${apiBase()}/api/web/auth/google`;
}

// ── getMe ───────────────────────────────────────────────────────────

export async function getMe(): Promise<AuthUser> {
  const response = await fetch(`${apiBase()}/api/web/auth/me`, {
    credentials: "include",
  });
  if (!response.ok) {
    return { authenticated: false };
  }
  return response.json() as Promise<AuthUser>;
}

// ── logout ──────────────────────────────────────────────────────────

export async function logout(): Promise<void> {
  await fetch(`${apiBase()}/api/web/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}

// ── requestMagicLink ────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<{ ok: boolean; message?: string; error?: string }> {
  const response = await fetch(`${apiBase()}/api/web/auth/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  return response.json() as Promise<{ ok: boolean; message?: string; error?: string }>;
}

// ── getTaskHistory ──────────────────────────────────────────────────

export async function getTaskHistory(ids?: string[]): Promise<{ tasks: TaskHistoryItem[]; total: number }> {
  const url =
    ids && ids.length > 0
      ? `${apiBase()}/api/web/tasks?ids=${ids.join(",")}`
      : `${apiBase()}/api/web/tasks`;
  const response = await fetch(url, {
    credentials: "include",
  });
  if (response.status === 401) {
    throw { code: "unauthenticated" };
  }
  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }
  return response.json() as Promise<{ tasks: TaskHistoryItem[]; total: number }>;
}

// ── streamTask — GET /api/web/tasks/:id/stream ─────────────────────

/**
 * Subscribes to an already-started task's SSE stream via a GET request.
 * Uses fetch+ReadableStream (not EventSource) so the bw_session cookie
 * is sent via credentials:'include'.
 *
 * Returns the same AsyncIterable<TaskEvent> shape as runTask.events.
 */
export async function streamTask(taskId: string): Promise<AsyncIterable<TaskEvent>> {
  let response: Response;
  try {
    response = await fetch(`${apiBase()}/api/web/tasks/${taskId}/stream`, {
      method: "GET",
      credentials: "include",
    });
  } catch (err) {
    throw { code: "network_error", message: String(err) };
  }

  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }

  if (!response.body) {
    throw { code: "no_body" };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  async function* generateEvents(): AsyncIterable<TaskEvent> {
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const gen = parseSSEChunk(buffer, chunk);

      for (const { buffer: newBuf, event } of gen) {
        buffer = newBuf;
        if (event) {
          yield event;
          if (event.type === "done" || event.type === "error") return;
        }
      }
    }
  }

  return generateEvents();
}

// ── getAgents ───────────────────────────────────────────────────────

import type { Agent } from "./types";

export async function getAgents(): Promise<Agent[]> {
  const res = await fetch(`${apiBase()}/api/web/agents`, { credentials: "include" });
  if (!res.ok) throw new Error(`getAgents failed: ${res.status}`);
  const body = await res.json();
  return body.agents as Agent[];
}

export async function getAgent(id: string): Promise<Agent> {
  const res = await fetch(`${apiBase()}/api/web/agents/${encodeURIComponent(id)}`, { credentials: "include" });
  if (res.status === 404) throw Object.assign(new Error("not_found"), { code: "not_found" });
  if (!res.ok) throw new Error(`getAgent failed: ${res.status}`);
  return (await res.json()) as Agent;
}

// ── createCheckout ──────────────────────────────────────────────────

export async function createCheckout(amountUsd: number): Promise<{ checkoutUrl: string; sessionId: string; amountUsd: number }> {
  const response = await fetch(`${apiBase()}/api/web/billing/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ amountUsd }),
  });
  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }
  return response.json() as Promise<{ checkoutUrl: string; sessionId: string; amountUsd: number }>;
}

// ── rateTask ────────────────────────────────────────────────────────

// ── planTask — POST /api/web/tasks/plan ───────────────────────────

export interface PlanAnalysis {
  clarificationNeeded: boolean;
  clarificationQuestion: string | null;
  categories: string[];
  complexity: "simple" | "medium" | "complex";
  tier: "fast" | "deep";
  minRealisticBudget: number;
  budgetAssessment: string;
  subtasks: Array<{
    description: string;
    category: string;
    estimatedBudgetShare: number;
    capabilitiesNeeded: string[];
  }>;
}

export interface PlanAgentRecord {
  id: string;
  name: string;
  priceMin: number;
  priceMax: number;
  rating: number;
  capabilities: string[];
  description?: string;
}

export interface PlanSubtask {
  id: string;
  description: string;
  category: string;
  assignedAgent: PlanAgentRecord | null;
  /** Up to 4 alternative agents (NEW — backend commits 68fd5fd + e099b9d). */
  candidateAgents?: PlanAgentRecord[];
  capabilitiesNeeded: string[];
  estimatedBudgetShare: number;
}

export interface PlanResponse {
  planId: string;
  analysis: PlanAnalysis;
  plan: {
    subtasks: PlanSubtask[];
    totalBudget: number;
    executionSteps: number;
  };
  budget: number;
}

export interface ConfirmResponse {
  taskId: string;
}

/**
 * Runs analysis + orchestration read-only pass, stashes the plan in Redis.
 * Returns planId + analysis + enriched plan. Anon-friendly.
 */
export async function planTask(
  prompt: string,
  budget?: number,
  agentTier?: "fast" | "deep",
  outputFormat?: "html" | "tsx" | "md",
  attachments?: Array<{ filename: string; mimeType: string; contentBase64: string }>,
): Promise<PlanResponse> {
  const body: Record<string, unknown> = { prompt };
  if (budget !== undefined) body.budget = budget;
  if (agentTier !== undefined) body.agentTier = agentTier;
  if (outputFormat !== undefined) body.outputFormat = outputFormat;
  if (attachments !== undefined && attachments.length > 0) body.attachments = attachments;

  let response: Response;
  try {
    response = await fetch(`${apiBase()}/api/web/tasks/plan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw { code: "network_error", message: String(err) };
  }

  if (response.status === 400) {
    let msg = "Bad request";
    try {
      const j = (await response.json()) as { error?: string; message?: string };
      msg = j.message ?? j.error ?? msg;
    } catch { /* empty */ }
    throw { code: "bad_request", message: msg };
  }

  if (response.status === 422) {
    let reason = "No orchestration plan";
    try {
      const j = (await response.json()) as { reason?: string };
      reason = j.reason ?? reason;
    } catch { /* empty */ }
    throw { code: "no_orchestration_plan", message: reason };
  }

  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }

  return response.json() as Promise<PlanResponse>;
}

/**
 * Confirms a previously planned task: creates the Task row, debits credits
 * (or claims the free-task slot), and kicks off orchestration.
 * Returns 202 { taskId }. FE calls streamTask(taskId) to watch progress.
 *
 * Throws typed errors:
 *   { code: 'gate_required' }            — anon 2nd task
 *   { code: 'insufficient_credits', credits, required } — authed w/o balance
 *   { code: 'plan_expired' }             — planId TTL elapsed
 *   { code: 'forbidden' }               — session mismatch
 */
export async function confirmTask(
  planId: string,
  overrideAgents?: Record<string, string>,
): Promise<ConfirmResponse> {
  const body: Record<string, unknown> = {};
  if (overrideAgents && Object.keys(overrideAgents).length > 0) {
    body.overrideAgents = overrideAgents;
  }

  let response: Response;
  try {
    response = await fetch(`${apiBase()}/api/web/tasks/${encodeURIComponent(planId)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw { code: "network_error", message: String(err) };
  }

  if (response.status === 402) {
    try {
      const j = (await response.json()) as { error?: string; credits?: number; required?: number };
      if (j.error === "insufficient_credits") {
        throw { code: "insufficient_credits", credits: j.credits, required: j.required };
      }
    } catch (parseErr) {
      const e = parseErr as { code?: string };
      if (e?.code === "insufficient_credits") throw parseErr;
    }
    throw { code: "gate_required" };
  }

  if (response.status === 410) {
    throw { code: "plan_expired" };
  }

  if (response.status === 403) {
    throw { code: "forbidden" };
  }

  if (response.status === 400) {
    let msg = "Bad request";
    try {
      const j = (await response.json()) as { error?: string; message?: string };
      msg = j.message ?? j.error ?? msg;
    } catch { /* empty */ }
    throw { code: "bad_request", message: msg };
  }

  if (!response.ok) {
    throw { code: "http_error", status: response.status };
  }

  return response.json() as Promise<ConfirmResponse>;
}

// ── getBookmarks / addBookmark / removeBookmark ─────────────────────

export interface Bookmark {
  id: string;
  agentId?: string;
  agentPeerId?: string;
  agentName?: string;
  agentDescription?: string;
  priceMin?: number;
  priceMax?: number;
  rating?: number;
  createdAt?: string;
}

export async function getBookmarks(): Promise<Bookmark[]> {
  const res = await fetch(`${apiBase()}/api/web/me/bookmarks`, {
    credentials: "include",
  });
  if (res.status === 401) return [];
  if (!res.ok) throw { code: "http_error", status: res.status };
  const body = await res.json() as { bookmarks: Bookmark[] };
  return body.bookmarks;
}

export async function addBookmark(
  input: { agentId?: string; agentPeerId?: string; agentName?: string },
): Promise<{ ok: true }> {
  const res = await fetch(`${apiBase()}/api/web/me/bookmarks`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw { code: "http_error", status: res.status };
  return { ok: true };
}

export async function removeBookmark(agentRef: string): Promise<{ ok: true }> {
  const res = await fetch(
    `${apiBase()}/api/web/me/bookmarks/${encodeURIComponent(agentRef)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) throw { code: "http_error", status: res.status };
  return { ok: true };
}

// ── getArtifacts ────────────────────────────────────────────────────

export interface Artifact {
  taskId: string;
  filename: string;
  mimeType: string;
  description?: string;
  size?: number;
  agentName?: string;
  completedAt?: string;
}

export async function getArtifacts(): Promise<Artifact[]> {
  const res = await fetch(`${apiBase()}/api/web/me/artifacts`, {
    credentials: "include",
  });
  if (res.status === 401) return [];
  if (!res.ok) throw { code: "http_error", status: res.status };
  const body = await res.json() as { artifacts: Artifact[]; total: number };
  return body.artifacts;
}

// ── rateTask ────────────────────────────────────────────────────────

export interface RateResponse { ok: boolean; reviewId?: string; reason?: string }

export async function rateTask(taskId: string, rating: number, comment?: string): Promise<RateResponse> {
  const res = await fetch(`${apiBase()}/api/web/tasks/${encodeURIComponent(taskId)}/rate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error((body as { error?: string }).error ?? `rateTask ${res.status}`), { code: (body as { error?: string }).error, status: res.status });
  }
  return await res.json() as RateResponse;
}
